import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ClosedDaysWarningDialog } from '../../../admin/components';
import {
    ControlledDatePicker,
    formatDateString,
    IParams,
    isAxiosError,
    isBadRequest,
    isConflictException,
} from '../../../shared';
import { useSavePlanningPattern } from '../../hooks/planning-pattterns.hook';
import { mapPatternFormToDto } from '../../mappers';
import { IPlanningPatternForm, ISavePattern } from '../../models';
import { usePlanningPatternEntrySchema } from '../../validators';
import { emptyWeek, PatternEntryInputs } from '../pattern-entry-inputs/pattern-entry-inputs.component';

interface Props {
    open: boolean;
    handleClose: () => void;
}

export const PatternDialog: FC<Props> = ({ open, handleClose }) => {
    const { t } = useTranslation();
    const { id } = useParams<keyof IParams>() as IParams;

    const { mutateAsync: createPattern, isPending: isSaving } = useSavePlanningPattern();
    const [closedDaysToConfirm, setClosedDaysToConfirm] = useState<{ pattern: ISavePattern; dates: string[] }>();
    const [onlyClosedDays, setOnlyClosedDays] = useState<string[]>([]);
    const form = useForm<IPlanningPatternForm>({
        resolver: yupResolver(usePlanningPatternEntrySchema()),
        mode: 'all',
    });

    const watchFeadProductsPattern = form.watch('feadProductsPattern');

    useEffect(() => {
        form.reset({ weeks: [emptyWeek] });
        setClosedDaysToConfirm(undefined);
        setOnlyClosedDays([]);
    }, [open, form]);

    useEffect(() => {
        form.setValue('weeks', [emptyWeek]);
    }, [form, watchFeadProductsPattern]);

    const savePattern = async (pattern: ISavePattern) => {
        setOnlyClosedDays([]);
        try {
            await createPattern({ planning: id, pattern });
            handleClose();
        } catch (e) {
            const skippedClosedDates: string[] =
                (isAxiosError(e) && (e.response?.data as any)?.skippedClosedDates) || [];
            if (isConflictException(e) && skippedClosedDates.length) {
                setClosedDaysToConfirm({ pattern, dates: skippedClosedDates });
            } else if (isBadRequest(e) && skippedClosedDates.length) {
                setOnlyClosedDays(skippedClosedDates);
            } else {
                throw e;
            }
        }
    };

    const onSave = (item: IPlanningPatternForm) => savePattern(mapPatternFormToDto(item));

    const onConfirmClosedDays = async () => {
        if (closedDaysToConfirm) {
            const { pattern } = closedDaysToConfirm;
            setClosedDaysToConfirm(undefined);
            await savePattern({ ...pattern, confirmClosedDays: true });
        }
    };

    return (
        <FormProvider {...form}>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth={watchFeadProductsPattern ? 'md' : 'xl'}>
                <DialogTitle>{t('newPattern')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Stack direction="row" spacing={2} sx={{ pt: 2, mb: 2 }}>
                        <ControlledDatePicker name="startDate" label={t('startDate')} required />
                        <ControlledDatePicker name="endDate" label={t('endDate')} required />
                    </Stack>
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        <PatternEntryInputs />
                    </Box>
                </DialogContent>
                {onlyClosedDays.length > 0 && (
                    <Alert severity="error" sx={{ mx: 3, mt: 2 }}>
                        {t('patternOnlyClosedDays')}
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {onlyClosedDays.map((date) => (
                                <li key={date}>{formatDateString(date)}</li>
                            ))}
                        </Box>
                    </Alert>
                )}
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={form.handleSubmit(onSave)} disabled={isSaving}>
                        {t('save')}
                    </Button>
                    <Button onClick={handleClose} color="secondary">
                        {t('cancel')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ClosedDaysWarningDialog
                open={!!closedDaysToConfirm}
                title={t('patternClosedDaysTitle')}
                text={t('patternClosedDaysText')}
                items={(closedDaysToConfirm?.dates || []).map(formatDateString)}
                confirmText={t('patternClosedDaysConfirm')}
                onConfirm={onConfirmClosedDays}
                onClose={() => setClosedDaysToConfirm(undefined)}
            />
        </FormProvider>
    );
};
